// src/components/interactive/RecipientExperienceComponent.js - Component displaying the recipient experience for encrypted emails
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import { motion, AnimatePresence } from 'framer-motion';

const ExperienceContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-lg);
`;

const ExperienceHeader = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ExperienceTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
`;

const ExperienceContent = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const IntroText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
  color: var(--text-color);
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-md);
  margin: var(--spacing-xl) 0 var(--spacing-lg);
`;

const StepDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  transition: background-color var(--transition-speed) ease;
`;

const StepControls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-xl);
`;

const StepNumber = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--primary-color);
  text-align: center;
`;

const RecipientInterface = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  margin: var(--spacing-lg) 0;
  background-color: var(--background-color);
`;

const GmailHeader = styled.div`
  background-color: var(--background-light);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-family: 'Roboto', sans-serif;
`;

const GmailLogo = styled.div`
  color: #4285f4;
  font-weight: bold;
  font-size: 1.2rem;
`;

const EmailSubject = styled.div`
  font-weight: 600;
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const EmailSender = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const SenderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #673ab7;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const SenderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SenderName = styled.div`
  font-weight: 600;
`;

const SenderEmail = styled.div`
  font-size: 0.8rem;
  color: var(--text-color-light);
`;

const EmailContent = styled.div`
  padding: var(--spacing-md);
  line-height: 1.6;
  background-color: var(--background-color);
  color: var(--text-color);
`;

const SecureMessageBox = styled.div`
  margin: var(--spacing-md) 0;
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--background-light);
  text-align: center;
`;

const SecureMessage = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--text-color);
`;

const SecureMessageButton = styled.div`
  display: inline-block;
  background-color: #0078d7;
  color: white;
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #0062b1;
  }
`;

const OutlookPortal = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--background-color);
`;

const OutlookHeader = styled.div`
  background-color: #0078d7;
  color: white;
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
`;

const BrowserAddressBar = styled.div`
  background-color: var(--background-light);
  padding: var(--spacing-xs) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  font-family: monospace;
  font-size: 0.8rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--text-color);
`;

const OutlookContent = styled.div`
  padding: var(--spacing-lg);
  text-align: center;
  background-color: var(--background-color);
  color: var(--text-color);
`;

const AuthOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  max-width: 400px;
  margin: var(--spacing-md) auto;
`;

const AuthOption = styled.div`
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: ${props => props.primary ? '#0078d7' : 'var(--background-color)'};
  color: ${props => props.primary ? 'white' : 'var(--text-color)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  transition: ${props => props.primary ? 'background-color 0.2s ease' : 'background-color 0.2s ease, color 0.2s ease'};
  
  &:hover {
    background-color: ${props => props.primary ? '#0062b1' : 'var(--background-light)'};
  }
`;

const PasscodeInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  max-width: 400px;
  margin: var(--spacing-lg) auto;
  text-align: left;
`;

const Input = styled.input`
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const PasscodeEmail = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  margin: var(--spacing-lg) 0;
  text-align: left;
  background-color: var(--background-light);
  color: var(--text-color);
`;

const PasscodeDisplay = styled.div`
  font-size: 2rem;
  font-weight: bold;
  letter-spacing: 2px;
  margin: var(--spacing-md) 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: var(--spacing-sm);
  position: relative;
  display: inline-block;
  color: var(--text-color);
  
  &:hover {
    background-color: rgba(0, 120, 215, 0.1);
    border-radius: var(--border-radius);
  }
  
  &:hover::after {
    content: "Click to auto-fill";
    position: absolute;
    bottom: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    white-space: nowrap;
    z-index: 10;
  }
  
  &:hover::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent rgba(0, 0, 0, 0.8) transparent;
    z-index: 10;
  }
`;

const EncryptionNote = styled.div`
  background-color: rgba(0, 120, 215, 0.1);
  border: 1px solid rgba(0, 120, 215, 0.3);
  color: #0078d7;
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

// Simplified tooltip component that appears directly above or beside the element it's pointing to
const InstructionTooltip = styled(motion.div)`
  position: absolute;
  background-color: #FF7A45;
  color: white;
  font-weight: 500;
  padding: 10px 15px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 100;
  pointer-events: none;
  font-size: 0.9rem;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
  }
  
  &.top::after {
    border-width: 8px 8px 0 8px;
    border-color: #FF7A45 transparent transparent transparent;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  &.bottom::after {
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent #FF7A45 transparent;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  &.left::after {
    border-width: 8px 0 8px 8px;
    border-color: transparent transparent transparent #FF7A45;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
  }
  
  &.right::after {
    border-width: 8px 8px 8px 0;
    border-color: transparent #FF7A45 transparent transparent;
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

// Simplified component for showing the current instruction
const Instruction = ({ show, text, direction, style }) => {
  if (!show) return null;
  
  return (
    <InstructionTooltip
      initial={{ opacity: 0, y: direction === 'top' ? 10 : direction === 'bottom' ? -10 : 0, x: direction === 'left' ? 10 : direction === 'right' ? -10 : 0 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0 }}
      className={direction}
      style={style}
    >
      {text}
    </InstructionTooltip>
  );
};

// Add back the CollapseButton styled component
const CollapseButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  margin-top: 4px;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const NoteBox = styled.div`
  background-color: rgba(255, 185, 0, 0.1);
  border-left: 4px solid var(--accent-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  margin: var(--spacing-lg) 0;
  position: relative;
  color: var(--text-color);

  ol, ul {
    margin-top: var(--spacing-xs);
    margin-bottom: 0;
    padding-left: var(--spacing-xl);
  }

  li {
    margin-bottom: var(--spacing-xs);
    padding-left: var(--spacing-xs);
  }

  li:last-child {
    margin-bottom: 0;
  }

  ul li {
    list-style-type: disc;
  }

  ul ul, ol ul {
    margin-top: var(--spacing-xs);
  }
`;

const ContinueButton = styled.div`
  color: ${props => props.disabled ? '#999' : 'blue'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: color 0.2s ease;
  opacity: ${props => props.disabled ? 0.7 : 1};
  
  &:hover {
    color: ${props => props.disabled ? '#999' : 'darkblue'};
    text-decoration: ${props => props.disabled ? 'none' : 'underline'};
  }
`;

const RecipientExperienceComponent = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [passcode, setPasscode] = useState('');
  const [passcodeClicked, setPasscodeClicked] = useState(false);
  const [activeInstruction, setActiveInstruction] = useState('');
  const [noteVisible, setNoteVisible] = useState({});
  
  // Set initial note visibility
  useEffect(() => {
    setNoteVisible({
      step1: false,
      step2: false,
      step3: false,
      step4: false,
      step5: false
    });
  }, []);
  
  // Show instructions based on current step
  useEffect(() => {
    // Clear any existing instruction
    setActiveInstruction('');
    
    // Set a timeout to show the new instruction
    const timer = setTimeout(() => {
      switch (currentStep) {
        case 1:
          setActiveInstruction('readMessage');
          break;
        case 2:
          setActiveInstruction('passcodeOption');
          break;
        case 3:
          setActiveInstruction('passcodeDisplay');
          
          // After showing passcode instruction, show continue instruction
          const continueTimer = setTimeout(() => {
            setActiveInstruction('continueButton');
          }, 7000);
          
          return () => clearTimeout(continueTimer);
        default:
          break;
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [currentStep]);
  
  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handlePasscodeChange = (e) => {
    setPasscode(e.target.value);
  };
  
  const autoFillPasscode = () => {
    setPasscode('98060514');
    setPasscodeClicked(true);
    
    // Reset the clicked state after animation
    setTimeout(() => {
      setPasscodeClicked(false);
    }, 1000);
  };
  
  const toggleNote = (step) => {
    setNoteVisible(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
  };
  
  return (
    <ExperienceContainer>
      <ExperienceHeader>
        <ExperienceTitle>Recipient Experience: Microsoft Office 365 Message Encryption</ExperienceTitle>
      </ExperienceHeader>
      
      <ExperienceContent>
        <IntroText>
          When you send an encrypted email to external recipients using Microsoft Office 365 Message Encryption, 
          they will go through the following experience to securely access your message. This walkthrough 
          demonstrates what your recipients will see and the steps they'll take.
        </IntroText>
        
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>Step 1: Recipient receives secure email notification in their inbox</StepNumber>
            
            <RecipientInterface>
              <GmailHeader>
                <GmailLogo>Gmail</GmailLogo>
              </GmailHeader>
              
              <EmailSubject>PHI: Encryption</EmailSubject>
              
              <EmailSender>
                <SenderAvatar>T</SenderAvatar>
                <SenderInfo>
                  <SenderName>Tonny Santana</SenderName>
                  <SenderEmail>to me ▾</SenderEmail>
                </SenderInfo>
              </EmailSender>
              
              <EmailContent>
                <div>Tonny Santana (<button>Tonny.Santana@aranscia.com</button>) has sent you a protected message.</div>
                
                <SecureMessageBox style={{ position: 'relative' }}>
                  <SecureMessage>🔒</SecureMessage>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <SecureMessageButton 
                      onClick={nextStep}
                      style={{ 
                        position: 'relative',
                        backgroundColor: '#0078d7',
                        padding: '8px 16px',
                        borderRadius: '4px'
                      }}
                    >
                      Read the message
                    </SecureMessageButton>
                    
                    <Instruction
                      show={activeInstruction === 'readMessage'}
                      text='Click "Read the message" to proceed to the secure portal'
                      direction="top"
                      style={{
                        width: '270px',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '8px'
                      }}
                    />
                  </div>
                </SecureMessageBox>
                
                <div>
                  <button>Learn about messages protected by Microsoft Purview Message Encryption.</button>
                </div>
              </EmailContent>
            </RecipientInterface>
            
            <NoteBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong>Note:</strong>
                <CollapseButton onClick={() => toggleNote('step1')}>
                  {noteVisible.step1 ? 'Hide Details' : 'Show Details'}
                  {noteVisible.step1 ? '▲' : '▼'}
                </CollapseButton>
              </div>
              
              <AnimatePresence>
                {noteVisible.step1 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    The recipient receives a notification email with a link to access the secure message. 
                    The email doesn't contain the actual PHI or sensitive content - this stays protected until the recipient 
                    authenticates themselves.
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!noteVisible.step1 && (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-color-light)' }}>
                  The recipient receives a notification email with a link...
                </div>
              )}
            </NoteBox>
          </motion.div>
        )}
        
        {currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>Step 2: Recipient clicks the link and is directed to the secure portal</StepNumber>
            
            <OutlookPortal>
              <BrowserAddressBar>outlook.office365.com/encryption/...</BrowserAddressBar>
              <OutlookHeader>Encrypted Message</OutlookHeader>
              
              <OutlookContent>
                <h3>Tonny.Santana@aranscia.com has sent you a protected message</h3>
                
                <div style={{ margin: 'var(--spacing-lg) 0' }}>🔒</div>
                
                <div>Sign in to view the message</div>
                
                <AuthOptions style={{ position: 'relative' }}>
                  <AuthOption primary>
                    <div>Sign in with Google</div>
                  </AuthOption>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <AuthOption 
                      onClick={nextStep}
                      style={{ position: 'relative' }}
                    >
                      <div>Sign in with a One-time passcode</div>
                    </AuthOption>
                    
                    <Instruction
                      show={activeInstruction === 'passcodeOption'}
                      text='Click "Sign in with a One-time passcode" to get a code sent to your email'
                      direction="top"
                      style={{
                        width: '300px',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '8px'
                      }}
                    />
                  </div>
                </AuthOptions>
              </OutlookContent>
            </OutlookPortal>
            
            <NoteBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong>Note:</strong>
                <CollapseButton onClick={() => toggleNote('step2')}>
                  {noteVisible.step2 ? 'Hide Details' : 'Show Details'}
                  {noteVisible.step2 ? '▲' : '▼'}
                </CollapseButton>
              </div>
              
              <AnimatePresence>
                {noteVisible.step2 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    The recipient has two options to authenticate: 
                    <ol>
                      <li>Sign in with their Google account (or Microsoft account/other supported identity providers)</li>
                      <li>Use a one-time passcode that will be sent to their email address</li>
                    </ol>
                    The one-time passcode method doesn't require the recipient to have any specific account or software.
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!noteVisible.step2 && (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-color-light)' }}>
                  The recipient has two options to authenticate...
                </div>
              )}
            </NoteBox>
          </motion.div>
        )}
        
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>Step 3: One-time passcode option</StepNumber>
            
            <OutlookPortal>
              <BrowserAddressBar>outlook.office365.com/encryption/one-time-password</BrowserAddressBar>
              
              <OutlookContent>
                <h3>We sent a one-time passcode to tassalso@gmail.com.</h3>
                
                <div>Please check your email, enter the one-time passcode and click continue. The one-time passcode will expire in 15 minutes.</div>
                
                <PasscodeInput>
                  <label>One-time passcode</label>
                  <Input 
                    type="text" 
                    placeholder="Enter passcode" 
                    value={passcode}
                    onChange={handlePasscodeChange}
                  />
                  
                  <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)', position: 'relative' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <ContinueButton 
                        onClick={passcode.length > 0 ? nextStep : undefined}
                        disabled={passcode.length === 0}
                        style={{ position: 'relative' }}
                      >
                        Continue
                      </ContinueButton>
                      
                      <Instruction
                        show={activeInstruction === 'continueButton'}
                        text='After entering the passcode, click "Continue" to proceed'
                        direction="top"
                        style={{
                          width: '300px',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginBottom: '8px'
                        }}
                      />
                    </div>
                    <div>This is a private computer. Keep me signed in for 12 hours.</div>
                  </div>
                </PasscodeInput>
                
                <div style={{ fontSize: '0.9rem', marginTop: 'var(--spacing-md)' }}>
                  Didn't receive the one-time passcode? Check your spam folder or <button style={{ color: 'blue', cursor: 'pointer' }}>get another one-time passcode</button>.
                </div>
              </OutlookContent>
            </OutlookPortal>
            
            <PasscodeEmail>
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <strong>Your one-time passcode to view the message</strong> 
                <span style={{ color: 'gray', marginLeft: 'var(--spacing-sm)' }}>Inbox</span>
              </div>
              
              <div>
                <div><strong>Microsoft Office 365 Message Encryption</strong></div>
                <div style={{ fontSize: '0.9rem', color: 'gray' }}>to me ▾</div>
              </div>
              
              <div style={{ margin: 'var(--spacing-md) 0' }}>Here is your one-time passcode</div>
              
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <PasscodeDisplay 
                  onClick={autoFillPasscode}
                  style={{
                    backgroundColor: passcodeClicked ? 'rgba(0, 120, 215, 0.2)' : 'transparent',
                    transform: passcodeClicked ? 'scale(1.05)' : 'scale(1)',
                    transition: 'background-color 0.3s ease, transform 0.3s ease',
                    padding: '10px',
                    border: '1px solid #FF8F50',
                    borderRadius: '4px',
                    display: 'inline-block',
                    position: 'relative'
                  }}
                >
                  98060514
                </PasscodeDisplay>
                
                <Instruction
                  show={activeInstruction === 'passcodeDisplay'}
                  text="Click on the passcode to automatically fill the form"
                  direction="right"
                  style={{
                    width: '230px',
                    top: '50%',
                    left: '110%',
                    transform: 'translateY(-50%)',
                    marginLeft: '8px'
                  }}
                />
              </div>
              
              <div>
                To view your message, enter the code in the web page where you requested it.
              </div>
              
              <div style={{ marginTop: 'var(--spacing-md)', color: 'gray' }}>
                NOTE: This one-time passcode expires 15 minutes after it was requested.
              </div>
            </PasscodeEmail>
            
            <NoteBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong>Note:</strong>
                <CollapseButton onClick={() => toggleNote('step3')}>
                  {noteVisible.step3 ? 'Hide Details' : 'Show Details'}
                  {noteVisible.step3 ? '▲' : '▼'}
                </CollapseButton>
              </div>
              
              <AnimatePresence>
                {noteVisible.step3 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    When the recipient chooses the one-time passcode option:
                    <ol>
                      <li>A passcode is automatically sent to their email address</li>
                      <li>They need to check their inbox for the passcode email</li>
                      <li>The passcode is valid for only 15 minutes</li>
                      <li>They enter the passcode on the secure portal to gain access to the encrypted message</li>
                    </ol>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!noteVisible.step3 && (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-color-light)' }}>
                  When the recipient chooses the one-time passcode option...
                </div>
              )}
            </NoteBox>
          </motion.div>
        )}
        
        {currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>Step 4: Recipient views the secure message</StepNumber>
            
            <OutlookPortal>
              <BrowserAddressBar>outlook.office365.com/encryption/display-message</BrowserAddressBar>
              
              <OutlookContent style={{ textAlign: 'left' }}>
                <h2>PHI: Encryption</h2>
                
                <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center', margin: 'var(--spacing-md) 0' }}>
                  <SenderAvatar>TS</SenderAvatar>
                  <div>
                    <div><strong>Tonny Santana</strong> &lt;Tonny.Santana@aranscia.com&gt;</div>
                    <div style={{ fontSize: '0.9rem', color: 'gray' }}>5/13/2023 6:13:44 PM</div>
                    <div style={{ fontSize: '0.9rem' }}>To: tassalso@gmail.com</div>
                  </div>
                </div>
                
                <EncryptionNote>
                  <span>🔒</span> Encrypt: This message is encrypted. Recipients can't remove encryption.
                </EncryptionNote>
                
                <div style={{ marginTop: 'var(--spacing-lg)' }}>
                  <p>Best regards,</p>
                  <p>Tonny Santana</p>
                  <p>Aranscia™ Precision Medicine</p>
                  <p>2bPrecise® | AccessDx Laboratory | YouScript®</p>
                  <hr />
                  <p>Email: tonny.santana@aranscia.com</p>
                  <p>LinkedIn: https://www.linkedin.com/company/aranscia</p>
                  <p>Web: www.aranscia.com</p>
                </div>
              </OutlookContent>
            </OutlookPortal>
            
            <NoteBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong>Note:</strong>
                <CollapseButton onClick={() => toggleNote('step4')}>
                  {noteVisible.step4 ? 'Hide Details' : 'Show Details'}
                  {noteVisible.step4 ? '▲' : '▼'}
                </CollapseButton>
              </div>
              
              <AnimatePresence>
                {noteVisible.step4 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    After successful authentication:
                    <ul>
                      <li>The recipient can view the encrypted message in their browser</li>
                      <li>The message displays a security banner indicating it's encrypted</li>
                      <li>The recipient can read the message contents securely without installing special software</li>
                      <li>The content remains encrypted and protected throughout the process</li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!noteVisible.step4 && (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-color-light)' }}>
                  After successful authentication, the recipient can view the encrypted message...
                </div>
              )}
            </NoteBox>
          </motion.div>
        )}
        
        {currentStep === 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StepNumber>Summary: Microsoft Office 365 Message Encryption Experience</StepNumber>
            
            <div style={{ textAlign: 'center' }}>
              <p>Microsoft Office 365 Message Encryption provides a seamless experience for external recipients to securely access PHI and other sensitive information without requiring them to create accounts or install special software.</p>
              
              <ul style={{ lineHeight: 1.6, textAlign: 'left', maxWidth: '80%', margin: '0 auto', paddingLeft: 'var(--spacing-xl)' }}>
                <li><strong>Initial notification:</strong> Recipients receive an email notification with a link to the secure message, not the actual content.</li>
                <li><strong>Authentication options:</strong> Recipients can authenticate using:
                  <ul style={{ paddingLeft: 'var(--spacing-lg)' }}>
                    <li>Their existing Microsoft/Google/Yahoo accounts</li>
                    <li>A one-time passcode sent to their email (expires in 15 minutes)</li>
                  </ul>
                </li>
                <li><strong>Secure viewing:</strong> After authentication, recipients can securely view the message in their browser.</li>
                <li><strong>Security indicators:</strong> Clear visual indicators show the message is encrypted and protected.</li>
              </ul>
              
              <NoteBox>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <strong>Important:</strong>
                  <CollapseButton onClick={() => toggleNote('step5')}>
                    {noteVisible.step5 ? 'Hide Details' : 'Show Details'}
                    {noteVisible.step5 ? '▲' : '▼'}
                  </CollapseButton>
                </div>
                
                <AnimatePresence>
                  {noteVisible.step5 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
                        <p>When sending PHI via encrypted email, remind recipients:</p>
                        <ul>
                          <li>To expect a notification email with a link to access the secure message</li>
                          <li>That they may need to check their spam/junk folder for the notification or passcode</li>
                          <li>That the one-time passcode expires after 15 minutes</li>
                          <li>That they don't need any special software to view the secure message</li>
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {!noteVisible.step5 && (
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-color-light)' }}>
                    When sending PHI via encrypted email, remind recipients to expect a notification email...
                  </div>
                )}
              </NoteBox>
            </div>
          </motion.div>
        )}
        
        <StepIndicator>
          <StepDot active={currentStep >= 1} />
          <StepDot active={currentStep >= 2} />
          <StepDot active={currentStep >= 3} />
          <StepDot active={currentStep >= 4} />
          <StepDot active={currentStep >= 5} />
        </StepIndicator>
        
        <StepControls>
          <Button 
            variant="secondary" 
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            Previous Step
          </Button>
          <div>{currentStep} of 5</div>
          <Button 
            onClick={nextStep}
            disabled={currentStep === 5}
          >
            Next Step
          </Button>
        </StepControls>
      </ExperienceContent>
    </ExperienceContainer>
  );
};

export default RecipientExperienceComponent; 